<script lang="ts">
  import { createEventDispatcher } from 'svelte';
  import { aiApi } from '../lib/api';
  import type { AIAnalysisData, TimeSeriesData } from '../lib/types';
  import { formatDate } from '../lib/utils';
  
  export let timeseriesId: string;
  export let timeseriesData: TimeSeriesData | null = null;
  export let aiAnalyses: AIAnalysisData[] = [];
  
  // Check if data is available (useful for future UI conditionals)
  $: dataAvailable = timeseriesData && timeseriesData.data_points && timeseriesData.data_points.length > 0;
  // Use the variable in a dummy block to prevent unused variable warning
  $: { void(dataAvailable); }
  
  const dispatch = createEventDispatcher();
  
  let loading = false;
  let error = '';
  const analysisQueries = [
    { id: 'summary', name: 'Generate Summary', icon: 'bi-file-earmark-text', description: 'Get a comprehensive overview of your time series data' },
    { id: 'patterns', name: 'Detect Patterns', icon: 'bi-graph-up', description: 'Identify recurring patterns and seasonality' },
    { id: 'anomalies', name: 'Find Anomalies', icon: 'bi-exclamation-triangle', description: 'Discover unusual data points and outliers' }
  ];
  
  // Get the most recent analysis of each type
  $: latestSummary = aiAnalyses.find(a => a.analysis_type === 'summary');
  $: latestPatterns = aiAnalyses.find(a => a.analysis_type === 'patterns');
  $: latestAnomalies = aiAnalyses.find(a => a.analysis_type === 'anomalies');
  
  // Run an analysis
  async function runAnalysis(analysisType: string) {
    try {
      loading = true;
      error = '';
      
      const analysis = await aiApi.analyze(timeseriesId, analysisType);
      
      // Add to analyses and notify parent
      aiAnalyses = [analysis, ...aiAnalyses];
      dispatch('analysisCreated', { analysis });
      
    } catch (err) {
      error = err instanceof Error ? err.message : 
              typeof err === 'string' ? err : 'Failed to run analysis';
      console.error('Error running analysis:', err);
    } finally {
      loading = false;
    }
  }
  
  // Helper to format date
  function formatAnalysisDate(analysis: AIAnalysisData | undefined): string {
    if (!analysis) return 'Never run';
    // Format date with fallback when created_at is missing
    return `Last run: ${analysis.created_at ? formatDate(analysis.created_at) : 'recently'}`;
  }
</script>

<div class="ai-insights card mb-4">
  <div class="card-header">
    <h5 class="mb-0">AI Insights</h5>
  </div>
  
  <div class="card-body">
    {#if error}
      <div class="alert alert-danger alert-dismissible fade show" role="alert">
        Error running analysis: {error}
        <button type="button" class="btn-close" on:click={() => error = ''} aria-label="Close"></button>
      </div>
    {/if}
    
    <div class="row">
      {#each analysisQueries as query}
        <div class="col-md-4 mb-3">
          <div class="card h-100">
            <div class="card-body d-flex flex-column">
              <div class="d-flex justify-content-between">
                <h6 class="card-title">
                  <i class="bi {query.icon} me-2"></i>
                  {query.name}
                </h6>
                <span class="badge {
                  query.id === 'summary' && latestSummary ? 'bg-success' :
                  query.id === 'patterns' && latestPatterns ? 'bg-success' :
                  query.id === 'anomalies' && latestAnomalies ? 'bg-success' :
                  'bg-secondary'
                }">
                  {query.id === 'summary' && latestSummary ? 'Available' :
                   query.id === 'patterns' && latestPatterns ? 'Available' :
                   query.id === 'anomalies' && latestAnomalies ? 'Available' :
                   'Not Available'}
                </span>
              </div>
              <p class="card-text mt-2">{query.description}</p>
              <p class="card-text text-muted small mt-auto">
                {query.id === 'summary' 
                  ? formatAnalysisDate(latestSummary)
                  : query.id === 'patterns'
                    ? formatAnalysisDate(latestPatterns)
                    : formatAnalysisDate(latestAnomalies)
                }
              </p>
              <button 
                class="btn btn-sm btn-primary mt-2" 
                on:click={() => runAnalysis(query.id)}
                disabled={loading}
              >
                {#if loading}
                  <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Processing...
                {:else}
                  <i class="bi bi-lightning-charge me-2"></i>
                  Run Analysis
                {/if}
              </button>
            </div>
          </div>
        </div>
      {/each}
    </div>
    
    {#if latestSummary || latestPatterns || latestAnomalies}
      <div class="ai-highlights mt-4">
        <h6 class="mb-3">Highlights from AI Analysis</h6>
        
        <div class="row">
          {#if latestSummary && latestSummary.results["summary"]}
            <div class="col-md-12 mb-3">
              <div class="card">
                <div class="card-body">
                  <h6 class="card-title">
                    <i class="bi bi-file-earmark-text me-2"></i>
                    Summary
                  </h6>
                  <p class="card-text">{latestSummary.results["summary"]}</p>
                </div>
              </div>
            </div>
          {/if}
          
          {#if latestPatterns && latestPatterns.results["overall_assessment"]}
            <div class="col-md-6 mb-3">
              <div class="card h-100">
                <div class="card-body">
                  <h6 class="card-title">
                    <i class="bi bi-graph-up me-2"></i>
                    Pattern Assessment
                  </h6>
                  <p class="card-text">{latestPatterns.results['overall_assessment']}</p>
                  
                  {#if latestPatterns.results['patterns'] && latestPatterns.results['patterns'].length > 0}
                    <h6 class="mt-3 mb-2">Top Patterns</h6>
                    <ul class="mb-0">
                      {#each latestPatterns.results['patterns'].slice(0, 3) as pattern}
                        <li>
                          {#if typeof pattern === 'object'}
                            <strong>{pattern.type}:</strong> {pattern.description}
                          {:else}
                            {pattern}
                          {/if}
                        </li>
                      {/each}
                    </ul>
                  {/if}
                </div>
              </div>
            </div>
          {/if}
          
          {#if latestAnomalies && latestAnomalies.results['overall_assessment']}
            <div class="col-md-6 mb-3">
              <div class="card h-100">
                <div class="card-body">
                  <h6 class="card-title">
                    <i class="bi bi-exclamation-triangle me-2"></i>
                    Anomaly Assessment
                  </h6>
                  <p class="card-text">{latestAnomalies.results['overall_assessment']}</p>
                  
                  {#if latestAnomalies.results['anomalies'] && latestAnomalies.results['anomalies'].length > 0}
                    <h6 class="mt-3 mb-2">Key Anomalies</h6>
                    <ul class="mb-0">
                      {#each latestAnomalies.results['anomalies'].slice(0, 3) as anomaly}
                        <li>
                          {#if typeof anomaly === 'object'}
                            <strong>{formatDate(anomaly.timestamp)}:</strong> {anomaly.description}
                          {:else}
                            {anomaly}
                          {/if}
                        </li>
                      {/each}
                    </ul>
                  {/if}
                </div>
              </div>
            </div>
          {/if}
        </div>
      </div>
    {/if}
  </div>
</div>

<style>
  .ai-insights {
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  }
  
  .card .card-title {
    font-size: 1rem;
    font-weight: 600;
  }
  
  ul {
    padding-left: 1.25rem;
  }
  
  .badge {
    font-weight: 500;
  }
</style>
